import { t, useLocale } from '../localization/adminText';
import { Badge, BlockStack, InlineStack, Text } from './admin-ui';

const countsValid = history => history && [history.delivered, history.refusals, history.total]
  .every(value => Number.isInteger(value) && value >= 0) && history.delivered + history.refusals === history.total;
const groups = [
  ['similar', 'Similar address'],
  ['different', 'Different address'],
  ['insufficient', 'Address comparison unavailable'],
];

function sourceLabel(history) {
  const sources = history?.sources;
  return sources?.local && sources?.shared ? 'Sources: this store and reviewed shared data.'
    : sources?.local ? 'Source: this store’s order results.'
      : sources?.shared ? 'Source: reviewed shared data.'
        : 'History is checked against this store and reviewed shared data.';
}

function relatedStatus(history) {
  if (history?.status === 'matched' || history?.status === 'no_history') {
    const valid = countsValid(history) && groups.every(([key]) => countsValid(history.address_matches?.[key]))
      && ['delivered', 'refusals', 'total'].every(field => groups.reduce((sum, [key]) => sum + history.address_matches[key][field], 0) === history[field]);
    if (!valid || (history.status === 'matched' ? history.total === 0 : history.total !== 0)) return 'unavailable';
  }
  return history?.status || 'unavailable';
}

export default function CodFulfillmentHistory({ order, enabled = null, detailed = false }) {
  const locale = useLocale();
  if (order?.type !== 'cod') return null;
  const history = order.fulfillment_history;
  let status = enabled === false || history?.status === 'disabled' ? 'disabled'
    : enabled === true ? history?.status || 'unavailable' : 'unavailable';
  const rateValid = typeof history?.delivery_rate === 'number' && Number.isFinite(history.delivery_rate)
    && history.delivery_rate >= 0 && history.delivery_rate <= 100;
  if (status === 'matched' && (!countsValid(history) || history.total === 0 || !rateValid)) status = 'unavailable';
  if (status === 'disabled' && !detailed) return null;

  const related = history?.related_phone;
  const relatedState = enabled === true && status !== 'disabled' ? relatedStatus(related) : 'unavailable';
  const rate = status === 'matched' ? new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 2 }).format(history.delivery_rate / 100) : null;
  const messages = {
    disabled: 'Delivery history reminders are disabled. Enable them in Risk management.',
    no_history: 'No confirmed orders match this phone and address.',
    insufficient_identity: 'Phone or address details are incomplete; exact history cannot be matched.',
    unavailable: 'Delivery history is temporarily unavailable.',
  };
  const relatedMessages = {
    no_history: 'No other confirmed orders were found for this phone.',
    insufficient_identity: 'A valid phone number is needed to find related records.',
    unavailable: 'Other records with this phone are temporarily unavailable.',
  };

  return <BlockStack gap="300">
    {detailed && <Text as="h3" variant="headingSm">{t('Fulfillment credit history')}</Text>}
    <section aria-label={t('Phone and address matching history')}>
      <BlockStack gap="200">
        {detailed && status !== 'disabled' && <Text as="h4" variant="headingSm">{t('Phone and address matching history')}</Text>}
        {status === 'matched' ? <>
          {!detailed && <Text as="p" tone="subdued">{t('Phone and address match')}</Text>}
          <InlineStack gap="200">
            {history.refusals > 0 && <Badge>{t('Has refusal history')}</Badge>}
            {history.delivered > 0 && <Badge tone="info">{t('Has delivery history')}</Badge>}
          </InlineStack>
          <Text as="p">{t('Historical delivery rate: {0}', [rate])}</Text>
          <Text as="p" tone="subdued">{t('Delivered: {0} · Refused: {1} · Confirmed orders: {2}', [history.delivered, history.refusals, history.total])}</Text>
        </> : <Text as="p" tone="subdued">{t(messages[status] || messages.unavailable)}</Text>}
        {detailed && ['matched', 'no_history'].includes(status) && <Text as="p" tone="subdued">{t(sourceLabel(history))}</Text>}
        {detailed && ['matched', 'no_history'].includes(status) && history.unlinked_parcels > 0 && <Text as="p" tone="subdued">{t('{0} parcels have no identifiable order and are excluded from the delivery rate.', [history.unlinked_parcels])}</Text>}
      </BlockStack>
    </section>
    {status !== 'disabled' && (detailed ? <section aria-label={t('Other records with the same phone')}>
      <BlockStack gap="200">
        <Text as="h4" variant="headingSm">{t('Other records with the same phone')}</Text>
        {relatedState === 'matched' ? <>
          <Text as="p">{t('Delivered: {0} · Refused: {1} · Confirmed orders: {2}', [related.delivered, related.refusals, related.total])}</Text>
          {groups.filter(([key]) => related.address_matches[key].total > 0).map(([key, label]) => {
            const counts = related.address_matches[key];
            return <Text as="p" tone="subdued" key={key}>{t('{0}: delivered {1} · refused {2} · orders {3}', [t(label), counts.delivered, counts.refusals, counts.total])}</Text>;
          })}
          <Text as="p" tone="subdued">{t(sourceLabel(related))}</Text>
          <Text as="p" tone="subdued">{t('The same phone does not confirm the same person. Check the address; these records are excluded from the matching history above.')}</Text>
        </> : <Text as="p" tone="subdued">{t(relatedMessages[relatedState] || relatedMessages.unavailable)}</Text>}
      </BlockStack>
    </section> : relatedState === 'matched' && <Text as="p" tone="subdued">{t('Other same-phone orders: {0} · Verify the address.', [related.total])}</Text>)}
    {detailed && status !== 'disabled' && <Text as="p" tone="subdued">{t('Delivery history provides the evidence. New orders are restricted only when a separate restriction rule is enabled.')}</Text>}
  </BlockStack>;
}
